import GenericApi from "@/api/genericApi";
import { InboxNotification, Loader } from "@/components";
import { InboxQueryKey } from "@/config/constants/QueryKeys";
import { ApiRoutes } from "@/config/routes/ApiRoutes";
import { ApiError, ApiResponse } from "@/types/Api";
import { InboxNotificationStatus, Inbox as Notifications } from "@/types/Inbox";
import CustomLogger from "@/utils/CustomLogger";
import { isBitramp } from "@/utils/utils";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React, { useState } from "react";

const Inbox: React.FC = () => {
  const [action, setAction] = useState<string | null>(null);

  const {
    data: notifications,
    isLoading,
    error,
  } = useQuery<ApiResponse<Notifications[]>>({
    queryFn: async () => GenericApi.get<Notifications[]>(`${ApiRoutes.INBOX}`),
    queryKey: [InboxQueryKey],
  });

  const groupNotificationByDate = (notifications: Notifications[]) => {
    return notifications.reduce((acc, notification) => {
      const date = new Date(notification.created_at)
        .toISOString()
        .split("T")[0];
      if (!acc[date]) {
        acc[date] = [];
      }
      acc[date].push(notification);
      return acc;
    }, {} as Record<string, Notifications[]>);
  };

  const updateNotificationStatus = useMutation<
    ApiResponse<unknown>,
    ApiError<unknown>,
    InboxNotificationStatus
  >({
    mutationFn: (data) =>
      GenericApi.post<InboxNotificationStatus>(
        ApiRoutes.NOTIFICATIONSTATUS,
        data
      ),
    onSuccess: async ({ data }) => {
      CustomLogger.log(data);
      queryClient.invalidateQueries({ queryKey: [InboxQueryKey] });
    },
    onError: async ({ error }) => {
      CustomLogger.error(error);
    },
  });

  const groupedNotifications = groupNotificationByDate(
    notifications?.data || []
  );

  const sortNotifications = (notifications: Notifications[]) => {
    return [...notifications].sort(
      (a, b) => Number(a.is_read) - Number(b.is_read)
    );
  };

  const sortedDates = Object.keys(groupedNotifications).sort(
    (a, b) => new Date(b).getTime() - new Date(a).getTime()
  );

  const [selectedNotifications, setSelectedNotifications] = useState<string[]>(
    []
  );
  const [isSelecting, setIsSelecting] = useState(false);

  const toggleSelectMode = () => {
    setIsSelecting((prev) => !prev);
    if (!isSelecting) {
      setSelectedNotifications([]);
    }
  };

  const handleCheckboxChange = (id: string) => {
    setSelectedNotifications((prev) =>
      prev.includes(id)
        ? prev.filter((notifId) => notifId !== id)
        : [...prev, id]
    );
  };

  const queryClient = useQueryClient();

  const formatDate = (date: string) => {
    if (!date) return;
    const d = new Date(date);
    const pad = (n: number) => n.toString().padStart(2, "0");

    return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`;
  };

  const handleDeleteNotifications = async () => {
    try {
      if (action === "delete") {
        for (const notificationId of selectedNotifications) {
          const id = Number(notificationId);
          if (!isNaN(id)) {
            await GenericApi.delete(`/notification/delete/${id}`);
            queryClient.invalidateQueries({ queryKey: [InboxQueryKey] });
          }
        }
      }
      if (action === "read") {
        updateNotificationStatus.mutate({
          notifications: selectedNotifications.map((id) => Number(id)),
          read: true,
        });
      }
      if (action === "unread") {
        updateNotificationStatus.mutate({
          notifications: selectedNotifications.map((id) => Number(id)),
          read: false,
        });
      }

      setSelectedNotifications([]);
      setShowConfirm(false);
      setAction(null);
    } catch (error) {
      console.error("Error deleting notifications:", error);
    }
  };

  const [showConfirm, setShowConfirm] = useState(false);

  if (isLoading) {
    return <Loader />;
  }

  if (error) {
    return <div>Error: {error.message}</div>;
  }

  return (
    <div className="inbox min-h-[100vh] mt-8 mb-20">
      {showConfirm && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white p-6 rounded-lg shadow-lg flex flex-col gap-4">
            <p className="text-lg font-semibold">
              {`Are you sure you want to ${
                action == "read"
                  ? "mark as read"
                  : action == "unread"
                  ? "mark as unread"
                  : "delete"
              } these notifications?`}
            </p>
            <div className="flex justify-end gap-4">
              <button
                onClick={() => {
                  setShowConfirm(false);
                  setAction(null);
                }}
                className="Outline_red_button"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteNotifications}
                className="Primary_button"
              >
                {action == "read"
                  ? "Mark as Read"
                  : action == "unread"
                  ? "Mark as Unread"
                  : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="flex justify-between items-center mb-3 px-8">
        <div className="Heading2 bold dark-blue">My Inbox</div>
        <div className="flex gap-4">
          {isSelecting && selectedNotifications.length > 0 ? (
            <div className="flex gap-4">
              <div
                className={isBitramp() ? "Outline_button" : "Primary_button"}
                onClick={() => {
                  setAction("read");
                  setShowConfirm(true);
                }}
              >
                {isBitramp() ? (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="17"
                    height="19"
                    viewBox="0 0 17 19"
                    fill="none"
                  >
                    <g clip-path="url(#clip0_4765_12754)">
                      <path
                        d="M16.8916 7.12587C16.6263 6.56575 16.1601 6.15902 15.724 5.74532C14.1364 4.23927 11.6996 2.19865 9.95503 0.864587C9.69427 0.667034 8.7897 0 8.57048 0C7.93359 0 4.04299 3.66287 3.26303 4.35083C1.72156 5.70813 0.318544 6.03816 0.138553 8.40881C-0.0275937 10.5889 -0.0668227 14.6445 0.143168 16.792C0.242394 17.8309 0.671605 19.007 1.87155 19L15.8532 18.8884C16.6747 18.5886 16.9078 17.8379 17.0001 17.0221L16.8916 7.12587ZM8.60971 2.28465L14.2218 7.02361L8.56817 11.7277L2.9192 7.02361L8.60971 2.28465ZM15.1471 17.1337H1.99385C2.2223 16.8548 1.99385 16.6596 1.99385 16.5527V8.99914C2.34922 8.95266 2.63536 9.17113 2.90304 9.36171C4.22529 10.2937 5.65369 11.902 6.95517 12.9502C7.59898 13.4662 8.32357 14.2053 9.14738 13.7637L15.1471 8.76673V17.1337Z"
                        fill="#EE33D9"
                      />
                    </g>
                    <defs>
                      <clipPath id="clip0_4765_12754">
                        <rect width="17" height="19" fill="white" />
                      </clipPath>
                    </defs>
                  </svg>
                ) : (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="17"
                    height="19"
                    viewBox="0 0 17 19"
                    fill="none"
                  >
                    <path
                      d="M16.8914 7.12587C16.626 6.56575 16.1599 6.15902 15.7238 5.74532C14.1361 4.23927 11.6993 2.19865 9.95479 0.864587C9.69403 0.667034 8.78946 0 8.57024 0C7.93334 0 4.04275 3.66287 3.26278 4.35083C1.72131 5.70813 0.3183 6.03816 0.138308 8.40881C-0.0278379 10.5889 -0.0670669 14.6445 0.142924 16.792C0.24215 17.8309 0.671361 19.007 1.87131 19L15.853 18.8884C16.6745 18.5886 16.9076 17.8379 16.9999 17.0221L16.8914 7.12587ZM8.60946 2.28465L14.2215 7.02361L8.56793 11.7277L2.91895 7.02361L8.60946 2.28465ZM15.1469 17.1337H1.99361C2.22206 16.8548 1.99361 16.6596 1.99361 16.5527V8.99914C2.34898 8.95266 2.63512 9.17113 2.9028 9.36171C4.22505 10.2937 5.65344 11.902 6.95492 12.9502C7.59874 13.4662 8.32332 14.2053 9.14713 13.7637L15.1469 8.76673V17.1337Z"
                      fill="white"
                    />
                  </svg>
                )}
                Mark as Read
              </div>
              <div
                className={isBitramp() ? "Outline_button" : "Primary_button"}
                onClick={() => {
                  setAction("unread");
                  setShowConfirm(true);
                }}
              >
                {isBitramp() ? (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="17"
                    height="13"
                    viewBox="0 0 17 13"
                    fill="none"
                  >
                    <path
                      d="M16.9012 1.64566C16.8254 0.939405 16.5219 0.509316 15.9747 0.0656451L1.18876 0C0.436936 0.199199 0.177131 0.683615 0.101258 1.41929C-0.225223 4.54989 0.365662 8.20112 0.101258 11.3928C0.202421 12.3549 0.604775 12.7646 1.59571 12.8642C5.92963 13.3033 10.9924 12.5133 15.4091 12.8687C16.246 12.8461 16.8529 11.9474 16.9012 11.1665C16.6414 8.12415 17.2185 4.62685 16.9012 1.64566ZM13.2134 1.76563L8.50469 5.79941C8.18511 5.81752 4.22136 2.39718 3.78681 1.76563H13.2134ZM15.0527 10.7069L14.7078 11.0465H1.94749V2.89744C2.34294 3.21888 2.82117 3.48372 3.21202 3.80289C4.51795 4.87359 5.80548 6.15027 7.1229 7.19607C7.68619 7.64653 8.33456 8.14453 9.07948 7.7665L15.0527 2.89744V10.7069Z"
                      fill="#EE33D9"
                    />
                  </svg>
                ) : (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="17"
                    height="13"
                    viewBox="0 0 17 13"
                    fill="none"
                  >
                    <g clip-path="url(#clip0_4516_8936)">
                      <path
                        d="M16.901 1.64566C16.8251 0.939405 16.5216 0.509316 15.9744 0.0656451L1.18852 0C0.436692 0.199199 0.176886 0.683615 0.101014 1.41929C-0.225467 4.54989 0.365418 8.20112 0.101014 11.3928C0.202177 12.3549 0.604531 12.7646 1.59547 12.8642C5.92939 13.3033 10.9921 12.5133 15.4088 12.8687C16.2457 12.8461 16.8527 11.9474 16.901 11.1665C16.6412 8.12415 17.2183 4.62685 16.901 1.64566ZM13.2131 1.76563L8.50445 5.79941C8.18487 5.81752 4.22111 2.39718 3.78657 1.76563H13.2131ZM15.0525 10.7069L14.7076 11.0465H1.94724V2.89744C2.3427 3.21888 2.82092 3.48372 3.21178 3.80289C4.5177 4.87359 5.80523 6.15027 7.12265 7.19607C7.68595 7.64653 8.33431 8.14453 9.07924 7.7665L15.0525 2.89744V10.7069Z"
                        fill="white"
                      />
                    </g>
                    <defs>
                      <clipPath id="clip0_4516_8936">
                        <rect width="17" height="13" fill="white" />
                      </clipPath>
                    </defs>
                  </svg>
                )}
                Mark as Unread
              </div>
              <div
                className={
                  isBitramp() ? "Delete_button_bitramp" : "Primary_button"
                }
                onClick={() => {
                  setAction("delete");
                  setShowConfirm(true);
                }}
              >
                Delete
              </div>
            </div>
          ) : (
            <div></div>
          )}
          <div
            className={`${
              isSelecting
                ? isBitramp()
                  ? "Cancel_button_bitramp"
                  : "Outline_button"
                : "Primary_button"
            }`}
            onClick={toggleSelectMode}
          >
            {isSelecting ? "Cancel" : "Select"}
          </div>
        </div>
      </div>
      <div className="Container px-8 py-8 flex flex-col gap-16 pshadow">
        <div
          className={`flex flex-col gap-8 h-[50rem] overflow-y-auto ${
            isBitramp() ? "crypto-list_bitramp" : "crypto-list"
          }`}
        >
          {sortedDates?.length > 0 ? (
            sortedDates.map((date) => (
              <div key={date} className="flex flex-col gap-4">
                <div className="Subtitle dark-gray bold">
                  {formatDate(date)}
                </div>
                <div className="flex flex-col gap-2 pr-[0.5rem]">
                  {sortNotifications(groupedNotifications[date]).map(
                    (notification) => (
                      <div
                        key={notification.id}
                        className="flex items-center gap-4"
                      >
                        {isSelecting && (
                          <input
                            type="checkbox"
                            className={
                              isBitramp()
                                ? "w-5 h-5 appearance-none rounded bg-[#464646] border border-gray-400 checked:bg-[#ee33d9] outline-none focus:outline-none focus:ring-0 active:ring-0 active:outline"
                                : "accent-gray-500 w-4 h-4"
                            }
                            checked={selectedNotifications.includes(
                              notification.id.toString()
                            )}
                            onChange={() =>
                              handleCheckboxChange(notification.id.toString())
                            }
                          />
                        )}
                        <InboxNotification
                          id={notification.id}
                          photo={notification.type}
                          message={notification.description}
                          time={new Date(
                            notification.created_at
                          ).toLocaleTimeString([], {
                            hour: "2-digit",
                            minute: "2-digit",
                            hour12: false,
                          })}
                          seen={notification.is_read}
                          inboxUser={
                            notification?.transaction_details?.user ===
                            undefined
                              ? null
                              : notification?.transaction_details?.user
                          }
                          inboxTransaction={
                            notification?.transaction_details?.transaction ===
                            undefined
                              ? null
                              : notification?.transaction_details?.transaction
                          }
                        />
                      </div>
                    )
                  )}
                </div>
              </div>
            ))
          ) : (
            <p>You don't have any notifications yet.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default Inbox;
